import { ReactNode } from 'react'
import { useAuth, SignInButton, SignUpButton } from '@clerk/clerk-react'
import { Loader2, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface AuthenticationGuardProps {
  children: ReactNode
}

// Removed Cloudflare Access authentication - using Clerk only

export function AuthenticationGuard({ children }: AuthenticationGuardProps) {
  const { isLoaded, isSignedIn } = useAuth()

  // Show loading while checking authentication
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  if (!isSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
            <CardTitle className="text-2xl font-bold">Welcome to Smokestack</CardTitle>
            <CardDescription>
              Secure authentication required to access your testing dashboard
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Authentication Methods */}
            <div className="space-y-4">
              <div className="text-sm font-medium text-gray-700 text-center">
                Sign in to access your dashboard:
              </div>

              <SignInButton mode="modal">
                <Button
                  variant="default"
                  className="w-full"
                  size="lg"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Sign In
                </Button>
              </SignInButton>

              <SignUpButton mode="modal">
                <Button
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  Create Account
                </Button>
              </SignUpButton>

              <div className="text-xs text-gray-500 text-center">
                Secure authentication powered by Clerk
              </div>
            </div>

            {/* Help Text */}
            <div className="text-xs text-gray-500 text-center space-y-1">
              <p>Protected by Clerk Authentication</p>
              <p>Your data is secure and encrypted</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}
